package dev.lingvo.site.pages.docs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.components.marketing.MarketingPageColumnStyle
import dev.lingvo.site.components.marketing.MarketingSectionStyle
import dev.lingvo.site.components.marketing.PublicFooter
import dev.lingvo.site.components.marketing.PublicHeader
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * What every file format can and cannot carry.
 *
 * **The page that blocks adoption.** A mobile team that has been burned by a localization tool asks
 * what it loses before they ask what it costs, and answering "nothing, really" loses them — because
 * they already know that is not true of any tool.
 *
 * Every figure comes from `GET /api/v1/formats`, which reads the format implementations themselves —
 * the same source `lingvo explain format` reads. A page carrying its own copy of this table is a page
 * that is wrong the first time a parser changes, and wrong in the direction of a promise.
 */
@Page("/docs/formats")
@Composable
fun FormatsPage() {
    LaunchedEffect(Unit) { document.title = tr("docs.formats.file_formats_lingvo", "File formats — Lingvo") }
    val state = loadAsync(Unit) { ApiClient.formats() }

    Div(Modifier.fillMaxWidth().toAttrs()) {
        PublicHeader()
        Main {
            Div(MarketingSectionStyle.toModifier().toAttrs()) {
                Column(MarketingPageColumnStyle.toModifier()) {
                    Column(Modifier.maxWidth(46.cssRem).gap(Space.MD)) {
                        H1 { Text(t("docs.formats.what_survives_a_round_trip", "What survives a round trip.")) }
                        Muted(
                            t("docs.formats.every_format_lingvo_reads_and_writes_2", "Every format Lingvo reads and writes, with what it carries and what it " +
                                "does not. Read from the parsers themselves rather than written down " +
                                "beside them, so this page cannot drift from the code that does the work."),
                        )
                    }

                    when (state) {
                        Async.Loading -> Muted(t("docs.formats.loading_formats", "Loading formats…"))
                        is Async.Failure -> Muted(
                            t("docs.formats.the_format_list_is_not_loading_2", "The format list is not loading right now. Everything on it is also in " +
                                "`lingvo explain format`, which reads the same endpoint."),
                        )
                        is Async.Success -> {
                            val formats = state.value.orEmpty()
                            LTable(Modifier.fillMaxWidth(), caption = t("docs.formats.file_formats", "File formats")) {
                                LTableHead {
                                    LTableRow {
                                        listOf(
                                            "Format",
                                            "Extension",
                                            "Plurals",
                                            "Comments",
                                            "Placeholders",
                                            "Lost on export",
                                        ).forEach { LTableHeaderCell { Text(it) } }
                                    }
                                }
                                LTableBody {
                                    formats.forEach { format ->
                                        LTableRow {
                                            LTableHeaderCell(scope = LTableHeaderScope.ROW) { Text(format.name) }
                                            LTableCell {
                                                Span(Modifier.monoFont().toAttrs()) { Text(format.extension) }
                                            }
                                            LTableCell {
                                                when {
                                                    !format.supportsPlurals ->
                                                        LBadge("no", variant = LBadgeVariant.WARNING)
                                                    // Naming the categories rather than saying "yes":
                                                    // PO numbers its forms against a header instead of
                                                    // naming CLDR categories, and a team shipping
                                                    // Russian needs to know which ones come back.
                                                    format.pluralCategories != null -> Span(
                                                        Modifier.monoFont().fontSize(TypeScale.Small).toAttrs(),
                                                    ) { Text(format.pluralCategories!!.joinToString(", ")) }
                                                    else -> LBadge("all", variant = LBadgeVariant.SUCCESS)
                                                }
                                            }
                                            LTableCell {
                                                if (format.supportsComments) {
                                                    LBadge("kept", variant = LBadgeVariant.SUCCESS)
                                                } else {
                                                    LBadge("dropped", variant = LBadgeVariant.WARNING)
                                                }
                                            }
                                            LTableCell {
                                                Span(Modifier.monoFont().fontSize(TypeScale.Small).toAttrs()) {
                                                    Text(format.placeholderSyntax)
                                                }
                                            }
                                            LTableCell {
                                                if (format.lostOnExport.isEmpty()) {
                                                    // The honest good answer, said plainly, because
                                                    // it is the whole reason to read the column.
                                                    Text(t("docs.formats.nothing", "Nothing"))
                                                } else {
                                                    Text(format.lostOnExport.joinToString("; "))
                                                }
                                            }
                                        }
                                    }
                                }
                            }

                            Column(Modifier.maxWidth(46.cssRem).gap(Space.MD)) {
                                Muted(
                                    t("docs.formats.key_order_is_preserved_on_export_2", "Key order is preserved on export for every format here, so a sync " +
                                        "produces a diff of the lines that changed rather than a diff " +
                                        "of the whole file."),
                                )
                                Muted(
                                    t("docs.formats.detection_is_by_path_values_strings_2", "Detection is by path: values/strings.xml is Android, " +
                                        "Localizable.strings is iOS, and anything ambiguous is asked " +
                                        "about rather than guessed."),
                                )
                                // The exit, offered before anybody asks for it.
                                Muted(
                                    t("docs.formats.you_can_leave_with_everything_at_2", "You can leave with everything at any time: lingvo export --all " +
                                        "writes every locale, plus your memory, glossary, comments and " +
                                        "history. And lingvo verify pushes, pulls and diffs against " +
                                        "the files on disk, exiting non-zero on any difference — " +
                                        "round-trip proof you run yourself rather than a claim on a page."),
                                )
                            }
                        }
                    }
                }
            }
        }
        PublicFooter()
    }
}

@Composable
private fun Muted(text: String) {
    P(
        Modifier
            .color(ColorMode.current.toSitePalette().textSecondary)
            .fontSize(TypeScale.Body)
            .margin(0.px)
            .toAttrs(),
    ) {
        Text(text)
    }
}
